import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { ApplicationListSortCol, TApplicationList, TApplicationListResponse, TApplicationListQuery, TApplicationListSortCol, adaptApplicationFromListResponse, adaptApplicationListQueryToRequest } from '../../types';
import { BACKEND_ADMIN_APPLICATION } from '../../backend-paths';


export const applicationListGetApi = createAsyncThunk(
	BACKEND_ADMIN_APPLICATION,
	(payload: TApplicationListQuery | undefined, thunkApi) => {
		const params = adaptApplicationListQueryToRequest(payload);

		return HttpService.get<TApplicationListResponse>(BACKEND_ADMIN_APPLICATION, { params }).then(result => {
			return adaptApplicationFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TApplicationList;
	query: TApplicationListQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: ApplicationListSortCol.CREATED_AT,
	},
	error: undefined,
};

export const applicationListSlice = createSlice({
	name: 'applicationListSlice',
	initialState,
	reducers: {
		setApplicationListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setApplicationListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setApplicationListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setApplicationListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setApplicationListQuerySortCol(state, action: PayloadAction<TApplicationListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearApplicationList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[applicationListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[applicationListGetApi.fulfilled.type]: (state, action: PayloadAction<TApplicationList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[applicationListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useApplicationListSelector = () => useAppSelector(state => state[applicationListSlice.name]);

export const { clearApplicationList, setApplicationListQueryLimit, setApplicationListQueryOffset, setApplicationListQuerySort, setApplicationListQuerySortCol, setApplicationListQuerySearch } = applicationListSlice.actions;
